import { Link } from "react-router-dom";
import { useSettings } from "@/lib/SettingsContext";
import usePageMeta from "@/lib/usePageMeta";
import { Star, Quote, Facebook, ArrowRight } from "lucide-react";

const Stars = ({ size = 14 }) => (
  <span className="inline-flex items-center gap-0.5 text-champagne">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

export default function Reviews() {
  usePageMeta(
    "Reviews · 100% 5-Star Rated · Beautify — Wadebridge, Cornwall",
    "Read genuine 5-star client reviews for Deborah Louise Beauty & Massage — 100% rating across Facebook and Google reviews. Massage, facials, lashes & nails in Wadebridge."
  );
  const { brand: BRAND, reviews: REVIEWS } = useSettings();
  return (
    <div data-testid="reviews-page" className="bg-grain">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-10 py-10 sm:py-16">
        <header className="text-center mb-10 sm:mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            Testimonials
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-plum-deep mt-4">
            What clients say
          </h1>
          <div className="mt-6 sm:mt-8 inline-flex items-center gap-3 sm:gap-4 bg-white border border-blush-deep/40 rounded-full px-5 sm:px-6 py-2.5 sm:py-3 shadow-sm">
            <Stars size={14} />
            <div className="text-xs sm:text-sm">
              <span className="font-semibold text-plum">{BRAND.reviewsSummary.rating}</span>{" "}
              <span className="text-foreground/60">
                · {BRAND.reviewsSummary.count} reviews on Facebook &amp; Google
              </span>
            </div>
          </div>
        </header>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
          {REVIEWS.map((r, i) => (
            <figure
              key={i}
              data-testid={`review-card-${i}`}
              className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-blush-deep/40 relative"
            >
              <Quote className="text-blush-deep" size={24} />
              <blockquote className="mt-4 text-foreground/80 leading-relaxed italic text-base sm:text-lg">
                "{r.text}"
              </blockquote>
              <figcaption className="mt-5 sm:mt-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-semibold text-plum">{r.name}</div>
                  <div className="text-xs text-foreground/55">
                    {r.treatment} · {r.date}
                  </div>
                </div>
                <Stars />
              </figcaption>
            </figure>
          ))}
        </div>

        <section className="mt-12 sm:mt-16 bg-plum text-white rounded-2xl sm:rounded-3xl p-8 sm:p-10 md:p-14 text-center">
          <Facebook className="mx-auto text-champagne" size={28} />
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl mt-4">
            Loved by clients on Facebook &amp; Google
          </h2>
          <p className="text-blush/85 mt-3 max-w-xl mx-auto">
            See Deborah's full client community — and leave your own review after
            your visit.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/review"
              data-testid="reviews-leave-review-cta"
              className="inline-flex items-center gap-2 rounded-full bg-champagne text-plum-deep px-7 py-3.5 text-sm font-semibold hover:bg-white transition"
            >
              Leave us a review <ArrowRight size={16} />
            </Link>
            <a
              href={BRAND.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-facebook-link"
              className="inline-flex items-center gap-2 rounded-full bg-white text-plum px-7 py-3.5 text-sm font-medium hover:bg-blush transition"
            >
              Visit Facebook page <ArrowRight size={16} />
            </a>
            <Link
              to="/contact"
              data-testid="reviews-book-cta"
              className="text-sm text-blush/90 underline-offset-4 hover:underline"
            >
              or book your treatment
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
